import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, CheckCircle, Warning, Receipt, Bank, Link as LinkIcon, LinkBreak, Info } from "@phosphor-icons/react";

function fmtMoney(n) {
  return Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " $";
}
function fmtDate(d) {
  if (!d) return "—";
  return d.slice(0, 10);
}
function confBadge(c) {
  if (c >= 0.9) return { label: "Excellent", class: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" };
  if (c >= 0.7) return { label: "Bon", class: "bg-sky-500/10 text-sky-400 border-sky-500/30" };
  return { label: "Faible", class: "bg-amber-500/10 text-amber-400 border-amber-500/30" };
}

export default function ReconciliationModule() {
  const [tab, setTab] = useState("matched");
  const [summary, setSummary] = useState(null);
  const [matched, setMatched] = useState([]);
  const [orphanReceipts, setOrphanReceipts] = useState({ total: 0, items: [] });
  const [orphanBank, setOrphanBank] = useState({ total: 0, items: [] });
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [s, m, or, ob] = await Promise.all([
        api.get("/reconciliation/summary"),
        api.get("/reconciliation/matched?limit=200"),
        api.get("/reconciliation/orphan-receipts?limit=100"),
        api.get("/reconciliation/orphan-bank?limit=100"),
      ]);
      setSummary(s.data);
      setMatched(m.data);
      setOrphanReceipts(or.data);
      setOrphanBank(ob.data);
    } catch (e) {
      toast.error("Impossible de charger les données");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const runAutoMatch = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reconciliation/auto-match");
      toast.success(`${r.data.created} nouveaux rapprochements créés`);
      await loadAll();
    } catch {
      toast.error("Échec de l'analyse");
    }
    setBusy(false);
  };

  const resetAll = async () => {
    if (!window.confirm("Supprimer tous les rapprochements automatiques ? (les manuels sont conservés)")) return;
    setBusy(true);
    try {
      await api.delete("/reconciliation/reset");
      toast.success("Rapprochements auto réinitialisés");
      await loadAll();
    } catch {
      toast.error("Échec");
    }
    setBusy(false);
  };

  const unmatch = async (id) => {
    if (!window.confirm("Défaire ce rapprochement ?")) return;
    try {
      await api.delete(`/reconciliation/match/${id}`);
      toast.success("Rapprochement défait");
      await loadAll();
    } catch {
      toast.error("Échec");
    }
  };

  const matchRate = summary
    ? summary.wellybox_total
      ? Math.round((summary.matched / summary.wellybox_total) * 100)
      : 0
    : 0;

  return (
    <div className="p-6 space-y-6" data-testid="reconciliation-module">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <ArrowsClockwise size={28} weight="bold" />
            Réconciliation Auto
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Rapproche automatiquement tes reçus WellyBox avec tes transactions bancaires Caisse Pop.
            <br />Même montant (±0.01 $) dans une fenêtre de ±5 jours.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="btn-auto-match"
            onClick={runAutoMatch}
            disabled={busy}
            className="px-4 py-2 bg-accent text-accent-foreground rounded-none hard-border hard-border-active text-sm font-semibold hover:bg-accent/90 disabled:opacity-50"
          >
            {busy ? "Analyse…" : "Lancer l'auto-match"}
          </button>
          <button
            data-testid="btn-reset-matches"
            onClick={resetAll}
            disabled={busy}
            className="px-4 py-2 rounded-none hard-border text-sm font-semibold hover:bg-muted disabled:opacity-50"
          >
            Tout réinitialiser (auto)
          </button>
        </div>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="reconciliation-kpis">
          <div className="hard-border p-4">
            <div className="text-xs uppercase text-muted-foreground tracking-wide">Reçus WellyBox</div>
            <div className="text-2xl font-bold mt-1">{summary.wellybox_total}</div>
          </div>
          <div className="hard-border p-4">
            <div className="text-xs uppercase text-muted-foreground tracking-wide">Dépenses bancaires</div>
            <div className="text-2xl font-bold mt-1">{summary.caisse_depense_total}</div>
          </div>
          <div className="hard-border p-4 bg-emerald-500/5">
            <div className="text-xs uppercase text-emerald-500 tracking-wide flex items-center gap-1">
              <CheckCircle size={14} weight="bold" /> Rapprochés
            </div>
            <div className="text-2xl font-bold mt-1">{summary.matched}</div>
            <div className="text-xs text-muted-foreground">{fmtMoney(summary.total_amount_matched)} — {matchRate}% des reçus</div>
          </div>
          <div className="hard-border p-4 bg-amber-500/5">
            <div className="text-xs uppercase text-amber-500 tracking-wide flex items-center gap-1">
              <Warning size={14} weight="bold" /> Orphelins
            </div>
            <div className="text-sm font-semibold mt-1">{summary.unmatched_receipts} reçus sans paiement</div>
            <div className="text-sm font-semibold">{summary.unmatched_bank} paiements sans reçu</div>
          </div>
        </div>
      )}

      {/* Info banner : explique le taux de match réaliste avec cartes de crédit */}
      {summary && summary.matched > 0 && summary.unmatched_receipts > 500 && (
        <div className="hard-border p-4 bg-sky-500/5 border-sky-500/30 text-sm flex gap-2">
          <Info size={20} weight="bold" className="text-sky-500 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-sky-400">Taux de rapprochement faible — c'est attendu.</div>
            <div className="text-muted-foreground mt-1">
              La plupart de tes factures (Zen Planner, Apple, Amazon, Tim Hortons, TELUS…) sont payées par <strong>VISA Desjardins</strong>, pas directement depuis ton compte courant. Ton compte bancaire ne voit qu'<strong>un seul paiement VISA mensuel groupé</strong> — impossible de le relier à 50+ reçus individuels sans le relevé VISA.
              <br/><br/>
              Les {summary.matched} rapprochements trouvés sont tes <strong>paiements directs</strong> (Interac, débit, virement). Pour obtenir une réconciliation complète, upload tes <strong>relevés VISA Desjardins</strong>.
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap hard-border-b">
        {[
          { id: "matched", label: `Rapprochés (${summary?.matched || 0})`, icon: CheckCircle },
          { id: "receipts", label: `Reçus orphelins (${orphanReceipts.total})`, icon: Receipt },
          { id: "bank", label: `Paiements orphelins (${orphanBank.total})`, icon: Bank },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${active ? "border-accent text-accent" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon size={16} weight="bold" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* TAB: Matched */}
      {tab === "matched" && (
        <div className="space-y-2" data-testid="matched-list">
          {matched.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-8">Aucun rapprochement pour l'instant. Clique sur "Lancer l'auto-match".</div>
          )}
          {matched.map(m => {
            const badge = confBadge(m.confidence);
            return (
              <div key={m.id} className="hard-border p-3 flex items-center gap-3 flex-wrap" data-testid="match-row">
                <div className={`text-xs px-2 py-1 border ${badge.class}`}>{badge.label} · {Math.round(m.confidence * 100)}%</div>
                <div className="text-lg font-bold tabular-nums">{fmtMoney(m.amount)}</div>
                <div className="text-xs text-muted-foreground">Écart: {m.days_diff}j</div>
                <div className="flex-1 grid md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1">
                    <Receipt size={14} className="text-muted-foreground" />
                    <span className="font-medium">{m.wellybox?._supplier || "?"}</span>
                    <span className="text-muted-foreground">· {fmtDate(m.wellybox_date)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Bank size={14} className="text-muted-foreground" />
                    <span className="truncate">{m.caisse?.description || "?"}</span>
                    <span className="text-muted-foreground whitespace-nowrap">· {fmtDate(m.caisse_date)}</span>
                  </div>
                </div>
                <button
                  data-testid="btn-unmatch"
                  onClick={() => unmatch(m.id)}
                  className="text-xs px-2 py-1 hard-border hover:bg-destructive hover:text-destructive-foreground flex items-center gap-1"
                >
                  <LinkBreak size={12} /> Défaire
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* TAB: Orphan receipts */}
      {tab === "receipts" && (
        <div className="space-y-2" data-testid="orphan-receipts-list">
          <p className="text-xs text-muted-foreground">Reçus WellyBox sans paiement bancaire détecté. Possibles raisons : payé en espèces, carte de crédit, autre compte, ou facture pas encore payée.</p>
          {orphanReceipts.items.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-8">Aucun reçu orphelin !</div>
          )}
          {orphanReceipts.items.map(t => (
            <div key={t.id} className="hard-border p-3 flex items-center gap-3 flex-wrap">
              <div className="text-lg font-bold tabular-nums w-28">{fmtMoney(t.amount)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{t._supplier || t.description}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(t.date)} · {t.category}</div>
              </div>
              {t._doc_number && <div className="text-xs text-muted-foreground">#{t._doc_number}</div>}
            </div>
          ))}
          {orphanReceipts.items.length < orphanReceipts.total && (
            <div className="text-xs text-center text-muted-foreground py-2">
              … et {orphanReceipts.total - orphanReceipts.items.length} autres (limité à 100)
            </div>
          )}
        </div>
      )}

      {/* TAB: Orphan bank */}
      {tab === "bank" && (
        <div className="space-y-2" data-testid="orphan-bank-list">
          <p className="text-xs text-muted-foreground">Dépenses bancaires sans reçu WellyBox associé. Possibles reçus perdus, ou dépenses purement personnelles à ne pas tracker.</p>
          {orphanBank.items.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-8">Aucune dépense orpheline !</div>
          )}
          {orphanBank.items.map(t => (
            <div key={t.id} className="hard-border p-3 flex items-center gap-3 flex-wrap">
              <div className="text-lg font-bold tabular-nums w-28">{fmtMoney(t.amount)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{t.description}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(t.date)} · {t.category} · {t._code}</div>
              </div>
            </div>
          ))}
          {orphanBank.items.length < orphanBank.total && (
            <div className="text-xs text-center text-muted-foreground py-2">
              … et {orphanBank.total - orphanBank.items.length} autres (limité à 100)
            </div>
          )}
        </div>
      )}
    </div>
  );
}
